import axios from 'axios';
import Cookies from 'js-cookie';

import {
	getJwtToken,
	getRefreshToken,
	setTokens,
	removeTokens,
	getKeepLogin,
} from './authService';

let isAlertShown = false; // 알림이 중복되지 않도록 관리

function createApiInstance() {
	const api = axios.create();

	api.interceptors.request.use(
		async config => {
			let token = getJwtToken();
			// 1. JWT 토큰이 있는 경우
			if (token) {
				config.headers.Authorization = `Bearer ${token}`;
				// 캐싱 방지 헤더 추가
				config.headers['Cache-Control'] = 'no-cache';
				config.headers['Pragma'] = 'no-cache';
				config.headers['Expires'] = '0';

				return config;
			} else {
				// 2. 토큰이 없을 때 refresh_token을 사용하여 갱신
				const refreshToken = getRefreshToken();
				const keepLoginFlag = getKeepLogin();

				if (refreshToken) {
					if (process.env.VUE_APP_BUILD_MODE === 'DEV') {
						console.log('토큰이 없어 리프레쉬 토큰으로 요청');
					}

					try {
						// 토큰 갱신 요청
						let param = {
							request_id: '',
							refresh_token: refreshToken,
							ip: await getIpInfoAPI(),
							platform: 'TMS_CLIENT',
							kep_login: keepLoginFlag ? 1 : 0,
						};

						const headers = {
							'Content-Type': 'application/json',
						};
						param = JSON.stringify(param);
						const url =
							process.env.VUE_APP_TMSURL + '/member/x/refresh_access_token';
						const response = await axios.post(url, param, { headers });

						// DEV 모드에서 로그 확인
						if (process.env.VUE_APP_BUILD_MODE === 'DEV') {
							console.log('기존 토큰 만료, 새로운 토큰 발급', response);
						}

						// 토큰 갱신 성공 시
						if (response.data.status.code === 0) {
							const tokensSet = setTokens(
								response.data.access_token,
								response.data.refresh_token,
								response.data.access_token_expired,
								response.data.refresh_token_expired,
								true,
							);

							if (tokensSet) {
								config.headers.Authorization = `Bearer ${response.data.access_token}`;
							} else {
								// 토큰 설정 실패 시, 알림 및 리디렉션
								handleTokenError();
								return Promise.reject(new Error('토큰이 만료되었습니다.'));
							}
						} else {
							// 갱신 요청이 실패한 경우
							handleTokenError();
							return Promise.reject(new Error('토큰이 만료되었습니다.'));
						}
					} catch (error) {
						// 요청 중 에러 발생 시 처리
						handleTokenError();
						return Promise.reject(error);
					}
				} else {
					// refreshToken이 없는 경우
					handleTokenError();
					return Promise.reject(new Error('토큰이 만료되었습니다.'));
				}
			}

			return config;
		},
		error => {
			return Promise.reject(error);
		},
	);

	return api;
}

// 토큰 오류 처리 함수
const handleTokenError = () => {
	if (!isAlertShown) {
		alert(
			'로그인 정보가 만료되었거나 찾을 수 없어 로그인 화면으로 이동합니다. 재로그인 후 사용해 주세요.',
		);
		isAlertShown = true;
	}
	removeTokens();
	window.location.href = '/tms/home';
};

// Refresh Token 처리 함수
async function handleRefreshToken() {
	const refreshToken = getRefreshToken(); // Refresh Token 가져오기
	const keepLoginFlag = getKeepLogin(); // "로그인 유지" 플래그 가져오기

	if (!refreshToken) {
		if (process.env.VUE_APP_BUILD_MODE === 'DEV') {
			console.warn('Refresh token이 없습니다.');
		}
		return null; // Refresh Token이 없으면 null 반환
	}
	if (process.env.VUE_APP_BUILD_MODE === 'DEV') {
		console.log('Refresh token으로 새로운 토큰 요청 중...');
	}
	try {
		// Refresh Token 요청에 필요한 매개변수 설정
		let param = {
			request_id: '',
			refresh_token: refreshToken,
			ip: await getIpInfoAPI(), // 클라이언트 IP 가져오기
			platform: 'TMS_CLIENT',
			kep_login: keepLoginFlag ? 1 : 0, // 로그인 유지 여부
		};

		const headers = {
			'Content-Type': 'application/json',
		};

		// JSON 문자열로 변환
		param = JSON.stringify(param);

		// Refresh Token 갱신 API 호출
		const url = process.env.VUE_APP_TMSURL + '/member/x/refresh_access_token';
		const response = await axios.post(url, param, { headers });

		// DEV 모드에서 응답 로그 출력
		if (process.env.VUE_APP_BUILD_MODE === 'DEV') {
			console.log('새로운 토큰 발급 요청 응답:', response);
		}

		// 토큰 갱신 성공 시
		if (response.data.status.code === 0) {
			const tokensSet = setTokens(
				response.data.access_token,
				response.data.refresh_token,
				response.data.access_token_expired,
				response.data.refresh_token_expired,
				true, // "로그인 유지" 플래그
			);

			if (tokensSet) {
				if (process.env.VUE_APP_BUILD_MODE === 'DEV') {
					console.log('새로운 토큰이 설정되었습니다.');
				}
				return response.data.access_token; // 새로운 Access Token 반환
			} else {
				if (process.env.VUE_APP_BUILD_MODE === 'DEV') {
					console.error('토큰 설정 실패');
				}
				handleTokenError();
				return null; // 실패 시 null 반환
			}
		} else {
			if (process.env.VUE_APP_BUILD_MODE === 'DEV') {
				console.error('Refresh token 갱신 실패:', response.data);
			}
			handleTokenError();
			return null;
		}
	} catch (error) {
		if (process.env.VUE_APP_BUILD_MODE === 'DEV') {
			console.error('토큰 갱신 중 오류 발생:', error);
		}
		handleTokenError();
		return null;
	}
}

export async function callAPI(
	url,
	data,
	isFileDownload = false,
	fileName = 'download.xlsx',
) {
	const start_time = performance.now();
	const api = createApiInstance();
	try {
		if (process.env.VUE_APP_BUILD_MODE === 'DEV') {
			console.log(url, 'request:', data);
		}

		// 파일 다운로드 요청인 경우 responseType 설정
		const config = isFileDownload ? { responseType: 'blob' } : {};
		const response = await api.post(url, data, config);
		// 파일 다운로드 처리
		if (isFileDownload) {
			// // 서버에서 파일명 추출
			// const contentDisposition = response.headers['content-disposition'];
			// const fileName = contentDisposition
			// 	? contentDisposition.split('filename=')[1].replace(/"/g, '') // 파일명 추출 및 따옴표 제거
			// 	: 'download.xlsx'; // 기본 파일명

			// Blob 생성 및 다운로드 처리
			const blob = new Blob([response.data], {
				type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
			});
			const downloadUrl = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = downloadUrl;
			link.setAttribute('download', fileName);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(downloadUrl);
			return { success: true };
		}

		const response_data = response.data;
		response_data.processing_time = Math.floor(performance.now() - start_time);
		if (process.env.VUE_APP_BUILD_MODE === 'DEV') {
			console.log(url, 'response:', response_data);
		}

		return response_data;
	} catch (error) {
		const httpCode = error.response ? error.response.status : null; // HTTP 상태 코드

		if (httpCode === 401) {
			// 토큰 만료 처리
			try {
				console.warn('Access token 만료, refresh token으로 갱신 중...');
				const newAccessToken = await handleRefreshToken();

				if (newAccessToken) {
					// 새로운 토큰으로 API 재시도
					const retryConfig = error.config; // 원래 요청의 설정
					retryConfig.headers.Authorization = `Bearer ${newAccessToken}`;
					const retryResponse = await api.request(retryConfig);
					return retryResponse.data;
				} else {
					// Refresh Token도 만료된 경우
					handleTokenError();
					return Promise.reject(new Error('토큰 갱신 실패. 로그인 필요.'));
				}
			} catch (refreshError) {
				console.error('Refresh token 갱신 중 오류 발생:', refreshError);
				handleTokenError();
				return Promise.reject(new Error('토큰 갱신 실패. 로그인 필요.'));
			}
		}

		console.error('Error in API call:', error);
		error.processing_time = Math.floor(performance.now() - start_time);
		return Promise.reject(error);
	}
}

//로그인을 위한 별도 api call 함수
export async function callAtlanAPI(url, data) {
	const start_time = performance.now(); // processing_time 기록
	const api = axios.create();
	try {
		if (process.env.VUE_APP_BUILD_MODE === 'DEV') {
			console.log(url, 'request:', data);
		}
		const headers = {
			'Content-Type': 'application/json',
		};
		const response = await api.post(url, data, { headers });
		if (process.env.VUE_APP_BUILD_MODE === 'DEV') {
			console.log('response:', response);
		}
		const response_data = response.data;
		response_data.processing_time = Math.floor(performance.now() - start_time);
		return response_data;
	} catch (error) {
		if (process.env.VUE_APP_BUILD_MODE === 'DEV') {
			console.log('error : ', error);
		}
		error.processing_time = Math.floor(performance.now() - start_time);
		error.status = {
			code: -1,
			message: 'server_error',
		};
		if (process.env.VUE_APP_BUILD_MODE === 'DEV') {
			console.error('atlan Api 요청 실패', error);
		}
		return error;
	}
}

//검색 호출을 위한 별도 api call 함수
export async function callSearchAPI(url, data) {
	const start_time = performance.now(); // processing_time 기록
	const api = axios.create();
	try {
		if (process.env.VUE_APP_BUILD_MODE === 'DEV') {
			console.log(url, 'request:', data);
		}
		const response = await api.get(url, { timeout: 5000 });
		if (process.env.VUE_APP_BUILD_MODE === 'DEV') {
			console.log('response:', response);
		}
		const response_data = response.data;
		response_data.processing_time = Math.floor(performance.now() - start_time);
		return response_data;
	} catch (error) {
		if (process.env.VUE_APP_BUILD_MODE === 'DEV') {
			console.log('error : ', error);
		}
		error.processing_time = Math.floor(performance.now() - start_time);
		error.status = {
			code: -1,
			message: 'server_error',
		};
		if (process.env.VUE_APP_BUILD_MODE === 'DEV') {
			console.error('주소검색 Api 요청 실패', error);
		}
		return error;
	}
}

export async function getIpInfoAPI() {
	try {
		const response = await axios.get('https://ipapi.co/json/');
		const ipInfo = response.data.ip; // 응답 데이터에서 IP 정보 추출
		return ipInfo;
	} catch (error) {
		if (process.env.VUE_APP_BUILD_MODE === 'DEV') {
			console.error('Error fetching IP info:', error);
		}
		return '';
	}
}

export function createApiInstanceWorker(token, refreshToken, keepLoginFlag) {
	const api = axios.create();

	api.interceptors.request.use(
		async config => {
			try {
				// 기존 토큰이 있을 경우
				if (token) {
					config.headers.Authorization = `Bearer ${token}`;
				} else if (refreshToken) {
					// Refresh Token을 사용하여 새로운 Access Token 발급 시도
					let param = {
						request_id: '',
						refresh_token: refreshToken,
						ip: await getIpInfoAPI(),
						platform: 'TMS_CLIENT',
						kep_login: keepLoginFlag ? 1 : 0,
					};
					const headers = {
						'Content-Type': 'application/json',
					};
					param = JSON.stringify(param);
					const url =
						process.env.VUE_APP_TMSURL + '/member/x/refresh_access_token';

					const response = await axios.post(url, param, { headers });
					if (process.env.VUE_APP_BUILD_MODE === 'DEV') {
						console.log('기존 토큰 만료, 새로운 토큰 발급', response);
					}

					if (response.data.status.code === 0) {
						// 새로운 토큰 설정
						const tokenSet = setTokens(
							response.data.access_token,
							response.data.refresh_token,
							response.data.access_token_expired,
							response.data.refresh_token_expired,
							true,
						);

						if (tokenSet) {
							config.headers.Authorization = `Bearer ${response.data.access_token}`;
						} else {
							console.warn('토큰 설정 실패. 재로그인이 필요합니다.');
							throw new Error('Token setting failed.');
						}
					} else {
						console.warn('토큰 갱신 실패. 재로그인이 필요합니다.');
						throw new Error('Token refresh failed.');
					}
				} else {
					// 토큰 및 갱신 토큰 모두 없을 경우
					console.warn('토큰이 유효하지 않거나 없습니다.');
					throw new Error('No valid token available.');
				}
			} catch (error) {
				if (process.env.VUE_APP_BUILD_MODE === 'DEV') {
					console.error('Request Interceptor Error:', error);
				}

				// 예외 처리 추가
				// 필요 시 사용자에게 경고 메시지를 표시하거나 로그인 화면으로 이동
				removeTokens();
				window.location.href = '/vrp_view/home';

				return Promise.reject(error);
			}
			return config;
		},
		error => {
			return Promise.reject(error);
		},
	);

	return api;
}
